import { ResendVerificationEmail } from '@/apis/users/requests'
import { useAppAlert } from '@/app/AppAlertProvider'
import { buttonVariants } from '@/app/common/components/Button'
import { CheckEmail } from '@/app/pages/authentication/CheckEmail.tsx'
import { ProgramSelectorView } from '@/app/pages/authentication/registration/components/ProgramSelectorView.tsx'
import { UserRegistrationView } from '@/app/pages/authentication/registration/components/UserRegistrationView.tsx'
import { AppRoutePaths } from '@/routing/route_paths.ts'
import AceScholarshipsLogo from '@assets/AceScholarshipLogo1.jpg'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { AuthLayout } from '../layout'

export const QueryURLKeys = {
  programID: 'program_id',
} as const

type View = 'program_form' | 'registration_form' | 'init_state' | 'check_email'

const RegistrationPage = () => {
  const { t: translate } = useTranslation()
  const [searchParams] = useSearchParams()
  const programID = searchParams.get(QueryURLKeys.programID)

  const [currentView, setView] = useState<View>(() => {
    return programID ? 'registration_form' : 'program_form'
  })
  const [registeredEmail, setRegisteredEmail] = useState<null | string>(null)
  const navigate = useNavigate()
  const { fireAlert } = useAppAlert()

  const renderRegistrationForm = () => {
    setView('registration_form')
  }

  const renderCheckEmail = (registeredEmail: string) => {
    setRegisteredEmail(registeredEmail)
    setView('check_email')
  }

  const handleResend = async () => {
    if (!registeredEmail) return
    const { error, data } = await ResendVerificationEmail(registeredEmail)
    if (error) {
      await fireAlert({
        variant: 'error',
        message: error.message,
      })
      navigate(AppRoutePaths.auth.login)
    } else {
      await fireAlert({
        variant: 'success',
        message: data.message,
      })
    }
  }

  if (currentView == 'check_email') {
    return (
      <AuthLayout>
        <AuthLayout.Content className="space-y-5">
          <CheckEmail onResend={handleResend}>
            <CheckEmail.Instructions className="mb-12">
              {translate('please_verify_your_email_by_clicking')}{' '}
              <span className="text-gray-700 font-medium">
                {registeredEmail}
              </span>
            </CheckEmail.Instructions>
          </CheckEmail>
        </AuthLayout.Content>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout>
      <AuthLayout.Content className="space-y-10">
        <div>
          <img
            alt={translate('ace_logo')}
            src={AceScholarshipsLogo}
            className="mx-auto h-auto"
            width="90%"
          />
          <h1 className="mt-3 text-center text-base font-medium text-gray-500">
            {translate('register')}
          </h1>
        </div>
        {(function () {
          if (currentView == 'registration_form' && programID)
            return (
              <UserRegistrationView
                programID={programID}
                renderNextView={renderCheckEmail}
              />
            )
          if (currentView == 'program_form')
            return (
              <ProgramSelectorView renderNextView={renderRegistrationForm} />
            )
          return null
        })()}
      </AuthLayout.Content>

      <p className="mt-10 text-center text-sm text-gray-700">
        {translate('already_have_an_account')}&nbsp;
        <Link
          to={AppRoutePaths.auth.login}
          className={buttonVariants({ variant: 'text', size: "text"})}
        >
          {translate('log_in')}
        </Link>
      </p>
    </AuthLayout>
  )
}

export default RegistrationPage
